import type { PortfolioAdminCard, PortfolioOrderSnapshot } from '@repo/api-contracts';
import { useEffect, useRef, useState } from 'react';
import { movedOrder, useReorderDrag } from '@/shared/lib';
import { Button, ContentCard } from '@/shared/ui';
import { useReorderHomePortfolios, useReorderPortfolios } from '../model';
import { PortfolioOrderCarousel } from './portfolio-order-carousel';
import { PortfolioOrderRow } from './portfolio-order-row';

export const PortfolioOrderEditor = ({
  snapshot,
  items,
  mode,
  onRefresh,
}: {
  snapshot?: PortfolioOrderSnapshot;
  items: readonly PortfolioAdminCard[];
  mode: 'all' | 'home';
  onRefresh: () => Promise<PortfolioOrderSnapshot | undefined>;
}) => {
  const all = useReorderPortfolios();
  const home = useReorderHomePortfolios();
  const action = mode === 'all' ? all : home;
  const [draft, setDraft] = useState(snapshot);
  const [dirty, setDirty] = useState(false);
  const seenToken = useRef(snapshot?.token);
  const [latest, setLatest] = useState<PortfolioOrderSnapshot | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const busy = action.isPending || refreshing;
  useEffect(() => {
    if (!snapshot || busy || (draft && seenToken.current === snapshot.token)) return;
    seenToken.current = snapshot.token;
    if (dirty) setLatest(snapshot);
    else {
      setDraft(snapshot);
      setLatest(null);
    }
  }, [draft, snapshot, dirty, busy]);
  const drag = useReorderDrag({
    scope: mode,
    axis: 'x',
    ids: draft?.orderedIds ?? [],
    disabled: busy || mode === 'home',
    onMove: (from, to) => {
      if (!draft || busy) return;
      setDraft({ ...draft, orderedIds: movedOrder(draft.orderedIds, from, to) });
      setDirty(true);
      setMessage(null);
    },
  });
  const save = async () => {
    if (!draft || busy) return;
    setMessage(null);
    setRefreshError(null);
    const input = { orderedIds: draft.orderedIds, expectedToken: draft.token };
    const result = mode === 'all' ? await all.reorder(input) : await home.reorderHome(input);
    if (result) {
      setDraft(result);
      setDirty(false);
      setLatest(null);
      setMessage('순서를 저장했습니다.');
    }
  };
  const refresh = async () => {
    if (busy) return;
    setRefreshing(true);
    setRefreshError(null);
    setMessage(null);
    try {
      const result = await onRefresh();
      if (result) {
        if (!draft) setDraft(result);
        else setLatest(result);
      } else setRefreshError('전체 순서를 불러오지 못했습니다. 다시 시도해주세요.');
    } catch {
      setRefreshError('전체 순서를 불러오지 못했습니다. 다시 시도해주세요.');
    } finally {
      setRefreshing(false);
    }
  };
  const move = (id: string, direction: 'UP' | 'DOWN') => {
    if (!draft || busy) return;
    const from = draft.orderedIds.indexOf(id);
    const to = from + (direction === 'UP' ? -1 : 1);
    if (from < 0 || to < 0 || to >= draft.orderedIds.length) return;
    setDraft({ ...draft, orderedIds: movedOrder(draft.orderedIds, from, to) });
    setDirty(true);
    setMessage(null);
  };
  return (
    <ContentCard
      aria-label={mode === 'all' ? '전체 포트폴리오 정렬' : '홈 포트폴리오 정렬'}
      title={mode === 'all' ? '전체 순서' : '홈 노출 관리'}
      description={
        mode === 'all'
          ? '카테고리와 페이지에 관계없이 전체 사례의 순서를 저장합니다.'
          : '홈에서는 기본 4개의 사례가 한 화면에 표시됩니다. 5개 이상 선택하면 좌우 버튼으로 나머지 사례를 볼 수 있습니다. 비공개 사례는 홈에 표시되지 않습니다.'
      }
    >
      <div className="space-y-3">
        {!draft ? (
          <p>전체 순서를 불러와야 정렬할 수 있습니다.</p>
        ) : (
          <PortfolioOrderCarousel count={draft.orderedIds.length}>
            {draft.orderedIds.map((id, index) => (
              <PortfolioOrderRow
                key={id}
                id={id}
                item={items.find((item) => item.id === id)}
                first={index === 0}
                last={index === draft.orderedIds.length - 1}
                disabled={busy}
                onMove={(direction) => move(id, direction)}
                handleProps={mode === 'all' ? drag.handleProps(id) : undefined}
                rowProps={mode === 'all' ? drag.rowProps(id) : undefined}
                dropEdge={
                  mode === 'all' && drag.target?.id === id
                    ? drag.target.after
                      ? 'after'
                      : 'before'
                    : undefined
                }
              />
            ))}
          </PortfolioOrderCarousel>
        )}
        {action.error || refreshError ? (
          <p role="alert" className="text-sm text-destructive">
            {refreshError ?? action.error}
          </p>
        ) : null}
        {message ? (
          <p role="status" className="text-sm">
            {message}
          </p>
        ) : null}
        {latest ? (
          <div className="rounded border p-3">
            <p>
              최신 순서: {latest.orderedIds.length}개 사례. 적용하면 작성 중인 순서를 교체합니다.
            </p>
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setDraft(latest);
                setDirty(false);
                setLatest(null);
                action.reset();
                setMessage(null);
              }}
            >
              최신 순서 적용
            </Button>
          </div>
        ) : null}
        <div className="flex gap-2">
          <Button type="button" disabled={!draft || busy} onClick={() => void save()}>
            {mode === 'all' ? '전체 순서 저장' : '홈 순서 저장'}
          </Button>
          {!draft || action.error || refreshError ? (
            <Button type="button" variant="outline" disabled={busy} onClick={() => void refresh()}>
              {draft ? '최신 순서 확인' : '순서 다시 불러오기'}
            </Button>
          ) : null}
        </div>
      </div>
    </ContentCard>
  );
};
